import { TemplateSummary, PreviewFormat, PREVIEW_FORMATS } from '@agency-smm/shared';
import { ExportQuality } from '../../utils/canvasRenderer';
import { SelectionBar, SelectionBarBtn, SelectionTemplateSelect } from './styled';

interface BulkSelectionBarProps {
  selectedPostIds: Set<string>;
  totalPosts: number;
  templates: TemplateSummary[];
  isBulkProcessing: boolean;
  bulkProgress: { current: number; total: number };
  previewFormat: PreviewFormat;
  pickedTemplateIds: string[];
  onApplyTemplate: (templateId: string) => Promise<void>;
  onRandomize: () => Promise<void>;
  onOpenPicker: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDownload: (quality: ExportQuality) => void;
  onDownloadCSV: (quality: ExportQuality) => void;
  onPreviewFormatChange: (next: PreviewFormat) => void;
  onSaveToGallery: () => Promise<void>;
  onBulkDelete: () => Promise<void>;
}

/**
 * Barra de acciones masivas que aparece cuando hay al menos un post
 * seleccionado en el grid: aplicar template, randomize, pick & cycle,
 * download, save to gallery, delete.
 */
export function BulkSelectionBar({
  selectedPostIds, totalPosts, templates,
  isBulkProcessing, bulkProgress, previewFormat, pickedTemplateIds,
  onApplyTemplate, onRandomize, onOpenPicker,
  onSelectAll, onClearSelection,
  onDownload, onDownloadCSV,
  onPreviewFormatChange, onSaveToGallery, onBulkDelete,
}: BulkSelectionBarProps) {
  if (selectedPostIds.size === 0) return null;

  const activeTemplates = templates.filter((t) => t.isActive !== false);

  return (
    <SelectionBar>
      <span>{selectedPostIds.size} selected</span>

      <SelectionTemplateSelect
        onChange={async (e) => {
          const templateId = e.target.value;
          if (!templateId) return;
          await onApplyTemplate(templateId);
          e.target.value = '';
        }}
        defaultValue=""
      >
        <option value="">Apply Template...</option>
        {activeTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </SelectionTemplateSelect>

      <SelectionBarBtn
        onClick={onRandomize}
        title={`Randomly distribute the ${activeTemplates.length} active templates across the ${selectedPostIds.size} selected posts`}
      >
        🎲 Randomize ({activeTemplates.length})
      </SelectionBarBtn>

      <SelectionBarBtn
        onClick={onOpenPicker}
        disabled={selectedPostIds.size === 0}
        title="Pick a subset of templates and apply them round-robin to the selected posts"
      >
        🎯 Pick & Cycle{pickedTemplateIds.length > 0 ? ` (${pickedTemplateIds.length})` : ''}
      </SelectionBarBtn>

      <SelectionBarBtn onClick={onSelectAll}>Select All</SelectionBarBtn>
      <SelectionBarBtn onClick={onClearSelection}>Clear</SelectionBarBtn>

      <SelectionBarBtn
        onClick={() => onDownload('mobile')}
        disabled={isBulkProcessing}
        title={`Download the ${selectedPostIds.size} selected post${selectedPostIds.size === 1 ? '' : 's'} as a ZIP, each with its currently applied template`}
        style={{ background: '#3B82F6', color: 'white', borderColor: '#3B82F6' }}
      >
        {isBulkProcessing
          ? `${bulkProgress.current}/${bulkProgress.total}`
          : `⬇ Download (${selectedPostIds.size})`}
      </SelectionBarBtn>

      <SelectionBarBtn
        onClick={() => onDownloadCSV('mobile')}
        disabled={isBulkProcessing || selectedPostIds.size === 0}
        title="Renders the composites (with template applied), uploads them to R2, and downloads only the CSV pointing to those URLs."
        style={{ background: 'rgba(255,255,255,0.18)' }}
      >
        {isBulkProcessing
          ? `${bulkProgress.current}/${bulkProgress.total}`
          : `📄 CSV (${selectedPostIds.size})`}
      </SelectionBarBtn>

      {/* Selector de formato: cambia el default global y persiste como override
          en cada post seleccionado (sobrescribe overrides previos). */}
      <select
        value={previewFormat}
        onChange={(e) => onPreviewFormatChange(e.target.value as PreviewFormat)}
        disabled={isBulkProcessing}
        title={selectedPostIds.size > 0
          ? `Applies the format to the ${selectedPostIds.size} selected post${selectedPostIds.size === 1 ? '' : 's'} (overwrites previous overrides)`
          : 'Default format for new posts and for Save to Gallery'}
        style={{
          padding: '5px 8px',
          borderRadius: 6,
          border: '1px solid rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.18)',
          color: 'white',
          fontSize: 12,
          fontWeight: 600,
          cursor: isBulkProcessing ? 'not-allowed' : 'pointer',
        }}
      >
        {(Object.keys(PREVIEW_FORMATS) as PreviewFormat[]).map((f) => (
          <option key={f} value={f} style={{ color: '#111' }}>
            {PREVIEW_FORMATS[f].label}
          </option>
        ))}
      </select>

      <SelectionBarBtn
        onClick={onSaveToGallery}
        disabled={isBulkProcessing}
        title={`Save ${selectedPostIds.size} post${selectedPostIds.size === 1 ? '' : 's'} to the Gallery as an immutable snapshot (format: ${PREVIEW_FORMATS[previewFormat].label})`}
        style={{ background: '#16a34a', color: 'white', borderColor: '#16a34a' }}
      >
        {isBulkProcessing
          ? `${bulkProgress.current}/${bulkProgress.total}`
          : `★ Save to Gallery (${selectedPostIds.size})`}
      </SelectionBarBtn>

      <SelectionBarBtn
        onClick={onBulkDelete}
        style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}
        title={`Delete ${selectedPostIds.size} selected post${selectedPostIds.size === 1 ? '' : 's'}`}
      >
        🗑 Delete ({selectedPostIds.size})
      </SelectionBarBtn>
      {/* totalPosts indica el grand total — exposed acá por si se quiere
          mostrar "{X} de {totalPosts}" en una iteración futura. */}
      <span style={{ display: 'none' }}>{totalPosts}</span>
    </SelectionBar>
  );
}
